import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const { FiSearch, FiEdit2, FiShield, FiUser, FiActivity, FiArrowLeft } = FiIcons;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      const { data, error } = await supabase
        .from('profiles_1772944081397')
        .select('*, companies_1773018500000(name)')
        .order('updated_at', { ascending: false });
      
      if (!error) setUsers(data);
      setLoading(false);
    };
    fetchUsers();
  }, []);

  const filtered = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <header className="mb-10">
          <h1 className="text-4xl font-black text-[#333333] font-ubuntu">User Management</h1>
          <p className="text-gray-500">Internal oversight of all application users and organizations.</p>
        </header>

        <div className="bg-white rounded-[2.5rem] border border-gray-200 shadow-xl overflow-hidden">
          <div className="p-8 border-b border-gray-100 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search by name or ID..." 
                className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-[#0758D1]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">User</th>
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">System Role</th>
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Organization</th>
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Last Activity</th>
                  <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan="5" className="p-20 text-center text-gray-400 italic">Fetching user directory...</td></tr>
                ) : (
                  filtered.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#0758D1] flex items-center justify-center font-bold">
                            {user.full_name?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <p className="font-bold text-[#333333] leading-tight">{user.full_name || 'Anonymous User'}</p>
                            <p className="text-[10px] text-gray-400 font-mono mt-1 uppercase">{user.id.split('-')[0]}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${user.system_role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                          {user.system_role}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <p className="text-sm font-medium text-gray-600">{user.companies_1773018500000?.name || 'No Company'}</p>
                        <p className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter">{user.company_role}</p>
                      </td>
                      <td className="px-8 py-6">
                        <p className="text-xs text-gray-500">{new Date(user.updated_at).toLocaleDateString()}</p>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <button className="p-2 text-gray-400 hover:text-[#0758D1] transition-colors">
                          <SafeIcon icon={FiEdit2} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;